<template>
  <div class="stale-status-page">
    <div class="stale-status-panel bg-white shadow-sm rounded">
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div>
          <h4 class="mb-1">
            Statuts non mis à jour
          </h4>
          <small class="text-muted">
            Colis synchronisés sans activité de statut depuis {{ staleFilters.hours }} heures.
          </small>
        </div>
        <div class="d-flex align-items-center mt-2 mt-md-0">
          <b-button
            variant="outline-info"
            class="mr-2 bg-white"
            :disabled="staleLoading"
            @click="scannerDrawerOpen = true"
          >
            <b-icon-search class="mr-1" />
            Scan Colis
          </b-button>
          <b-button
            variant="outline-danger"
            class="mr-2 bg-white"
            :disabled="staleLoading"
            @click="fetchStaleOutsourcingOrders"
          >
            <b-icon-arrow-clockwise class="mr-1" />
            Rafraîchir
          </b-button>
        </div>
      </div>

      <div class="stale-summary d-flex flex-wrap mb-3">
        <div class="stale-metric mr-3 mb-2">
          <span class="text-muted d-block">Total</span>
          <strong>{{ staleSummary.total || 0 }}</strong>
        </div>
        <div class="stale-metric mr-3 mb-2">
          <span class="text-muted d-block">Sélectionnés</span>
          <strong>{{ selectedStaleOrderIds.length }}</strong>
        </div>
        <div class="stale-metric mb-2">
          <span class="text-muted d-block">Seuil</span>
          <strong>{{ staleFilters.hours }}h</strong>
        </div>
      </div>

      <div class="d-flex flex-wrap align-items-center mb-3">
        <b-form-input
          v-model="staleFilters.search"
          type="search"
          placeholder="Rechercher BL, code externe, client..."
          class="mr-2 mb-2"
          style="max-width: 320px"
          @keyup.enter="applyStaleFilters"
        />
        <b-form-select
          v-model="staleFilters.companyId"
          :options="staleCompanyOptions"
          class="mr-2 mb-2"
          style="max-width: 260px"
          @change="applyStaleFilters"
        />
        <b-form-select
          v-model="staleFilters.status"
          :options="staleStatusOptions"
          class="mr-2 mb-2"
          style="max-width: 220px"
          @change="applyStaleFilters"
        />
        <b-button
          variant="outline-secondary"
          class="mb-2 bg-white"
          @click="resetStaleFilters"
        >
          Réinitialiser
        </b-button>
      </div>

      <b-overlay
        :show="staleLoading"
        rounded
        opacity="0.65"
      >
        <b-table
          :items="staleOrders"
          :fields="staleTableFields"
          responsive
          hover
          show-empty
          empty-text="Aucun colis synchronisé en retard de statut."
          class="sync-table mb-0"
        >
          <template #head(selected)>
            <b-form-checkbox
              :checked="allStaleOrdersSelected"
              :disabled="!staleOrders.length"
              @change="toggleAllStaleOrders"
            />
          </template>
          <template #cell(selected)="row">
            <b-form-checkbox
              :checked="selectedStaleOrderIds.includes(row.item.id)"
              @change="toggleStaleOrder(row.item.id)"
            />
          </template>
          <template #cell(order)="row">
            <router-link :to="{ name: 'order-show', params: { id: row.item.id } }">
              <strong>{{ row.item.id }}</strong>
            </router-link>
            <small class="text-muted d-block">{{ row.item.barcode || row.item.external_barcode || '-' }}</small>
          </template>
          <template #cell(status_label)="row">
            <b-badge variant="warning">
              {{ row.item.status_label }}
            </b-badge>
          </template>
          <template #cell(outsourcing_company)="row">
            {{ row.item.outsourcing_company.display_name || row.item.outsourcing_company.name || '-' }}
          </template>
          <template #cell(supplier)="row">
            {{ row.item.supplier.username || '-' }}
          </template>
          <template #cell(last_activity_at)="row">
            {{ formatDateTime(row.item.last_activity_at) }}
            <small class="text-muted d-block">{{ row.item.stale_hours }}h</small>
          </template>
          <template #cell(actions)="row">
            <b-button
              variant="outline-danger"
              size="sm"
              class="bg-white"
              :disabled="staleRefreshing"
              @click="refreshStaleOrders([row.item.id])"
            >
              <b-icon-arrow-clockwise />
            </b-button>
          </template>
        </b-table>
      </b-overlay>

      <div class="d-flex justify-content-between align-items-center mt-3">
        <small class="text-muted">
          {{ stalePagination.total ? stalePagination.offset + 1 : 0 }} - {{ Math.min(stalePagination.offset + stalePagination.limit, stalePagination.total) }}
          sur {{ stalePagination.total }}
        </small>
        <div>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-2 bg-white"
            :disabled="stalePagination.offset === 0 || staleLoading"
            @click="previousStalePage"
          >
            Précédent
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="bg-white"
            :disabled="stalePagination.offset + stalePagination.limit >= stalePagination.total || staleLoading"
            @click="nextStalePage"
          >
            Suivant
          </b-button>
        </div>
      </div>
    </div>

    <b-toast
      v-if="alertVisible"
      :variant="alertVariant"
      :auto-hide-delay="3500"
      solid
      toaster="b-toaster-top-right"
      @hidden="alertVisible = false"
    >
      {{ alertMessage }}
    </b-toast>

    <!-- Order Status Scanner Drawer -->
    <order-status-scanner-drawer
      :open.sync="scannerDrawerOpen"
      :stale-hours="staleFilters.hours"
    />
  </div>
</template>

<script>
import {
  BBadge, BButton, BFormCheckbox, BFormInput, BFormSelect, BIconArrowClockwise,
  BIconSearch, BOverlay, BTable, BToast,
} from 'bootstrap-vue'
import axios from 'axios'
import OrderStatusScannerDrawer from '@/components/OrderStatusScannerDrawer.vue'

export default {
  name: 'StaleOutsourcingStatus',
  components: {
    BBadge,
    BButton,
    BFormCheckbox,
    BFormInput,
    BFormSelect,
    BIconArrowClockwise,
    BIconSearch,
    BOverlay,
    BTable,
    BToast,
    OrderStatusScannerDrawer,
  },
  data() {
    return {
      outsourcingCompanies: [],
      staleLoading: false,
      staleRefreshing: false,
      scannerDrawerOpen: false,
      alertVisible: false,
      alertVariant: '',
      alertMessage: '',
      staleOrders: [],
      staleSummary: {},
      selectedStaleOrderIds: [],
      staleFilters: {
        search: '',
        companyId: '',
        status: 'all',
        hours: 8,
      },
      stalePagination: {
        limit: 50,
        offset: 0,
        total: 0,
      },
      staleTableFields: [
        { key: 'selected', label: '', class: 'stale-select-col' },
        { key: 'order', label: 'Colis' },
        { key: 'status_label', label: 'Statut' },
        { key: 'outsourcing_company', label: 'Société externe' },
        { key: 'external_id', label: 'Code externe' },
        { key: 'supplier', label: 'Fournisseur' },
        { key: 'last_activity_at', label: 'Dernière activité' },
        { key: 'actions', label: 'Actions', class: 'text-right' },
      ],
    }
  },
  computed: {
    staleCompanyOptions() {
      return [
        { value: '', text: 'Toutes les sociétés' },
        ...this.outsourcingCompanies.map(company => ({
          value: company.id,
          text: company.display_name || company.name,
        })),
      ]
    },
    staleStatusOptions() {
      const options = [{ value: 'all', text: 'Tous les statuts' }]
      const statuses = this.staleSummary.by_status || []
      statuses.forEach(item => {
        options.push({
          value: item.status,
          text: `${item.status_label} (${item.count})`,
        })
      })
      return options
    },
    allStaleOrdersSelected() {
      return this.staleOrders.length > 0
        && this.staleOrders.every(order => this.selectedStaleOrderIds.includes(order.id))
    },
  },
  created() {
    this.fetchInitialData()
  },
  methods: {
    async fetchInitialData() {
      try {
        const { data } = await axios.get('/api/companies/outsourcing-company/')
        this.outsourcingCompanies = data.results || data || []
      } catch (error) {
        this.showAlert('danger', 'Erreur lors du chargement des sociétés externes')
      }
      await this.fetchStaleOutsourcingOrders()
    },
    formatDateTime(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    async fetchStaleOutsourcingOrders() {
      this.staleLoading = true
      try {
        const params = {
          limit: this.stalePagination.limit,
          offset: this.stalePagination.offset,
          hours: this.staleFilters.hours,
        }
        if (this.staleFilters.search) params.search = this.staleFilters.search
        if (this.staleFilters.companyId) params.company_id = this.staleFilters.companyId
        if (this.staleFilters.status && this.staleFilters.status !== 'all') params.status = this.staleFilters.status

        const { data } = await axios.get('/api/orders/stale-outsourcing-status/', { params })
        this.staleOrders = data.orders || []
        this.staleSummary = data.summary || {}
        this.stalePagination.total = data.total || 0
        this.stalePagination.limit = data.limit || this.stalePagination.limit
        this.stalePagination.offset = data.offset || 0
        this.selectedStaleOrderIds = this.selectedStaleOrderIds.filter(id => this.staleOrders.some(order => order.id === id))
      } catch (error) {
        this.showAlert('danger', 'Erreur lors du chargement des statuts non mis à jour')
      } finally {
        this.staleLoading = false
      }
    },
    resetStaleFilters() {
      this.staleFilters.search = ''
      this.staleFilters.companyId = ''
      this.staleFilters.status = 'all'
      this.applyStaleFilters()
    },
    applyStaleFilters() {
      this.stalePagination.offset = 0
      this.fetchStaleOutsourcingOrders()
    },
    toggleStaleOrder(orderId) {
      if (this.selectedStaleOrderIds.includes(orderId)) {
        this.selectedStaleOrderIds = this.selectedStaleOrderIds.filter(id => id !== orderId)
      } else {
        this.selectedStaleOrderIds = [...this.selectedStaleOrderIds, orderId]
      }
    },
    toggleAllStaleOrders(checked) {
      if (checked) {
        const currentIds = this.staleOrders.map(order => order.id)
        this.selectedStaleOrderIds = Array.from(new Set([...this.selectedStaleOrderIds, ...currentIds]))
      } else {
        const currentIds = this.staleOrders.map(order => order.id)
        this.selectedStaleOrderIds = this.selectedStaleOrderIds.filter(id => !currentIds.includes(id))
      }
    },
    async refreshStaleOrders(orderIds) {
      if (!orderIds.length) return
      this.staleRefreshing = true
      try {
        const { data } = await axios.post('/api/orders/stale-outsourcing-status/refresh/', {
          order_ids: orderIds,
        })
        const results = data.results || {}
        const queued = (results.queued || []).length
        const unsupported = (results.unsupported || []).length
        const failed = (results.failed || []).length
        const variant = failed ? 'warning' : 'success'
        this.showAlert(
          variant,
          `${queued} synchronisation(s) lancée(s), ${unsupported} non supportée(s), ${failed} échec(s).`,
        )
        this.selectedStaleOrderIds = []
        await this.fetchStaleOutsourcingOrders()
      } catch (error) {
        this.showAlert('danger', 'Erreur lors de la resynchronisation des statuts')
      } finally {
        this.staleRefreshing = false
      }
    },
    previousStalePage() {
      this.stalePagination.offset = Math.max(this.stalePagination.offset - this.stalePagination.limit, 0)
      this.fetchStaleOutsourcingOrders()
    },
    nextStalePage() {
      this.stalePagination.offset += this.stalePagination.limit
      this.fetchStaleOutsourcingOrders()
    },
    showAlert(variant, message) {
      this.alertVariant = variant
      this.alertMessage = message
      this.alertVisible = true
    },
  },
}
</script>

<style lang="scss" scoped>
.stale-status-page {
  padding: 2rem;
}
.stale-status-panel {
  padding: 1.5rem;
}
.sync-table {
  margin-top: 1rem;
}
.stale-metric {
  min-width: 110px;
  padding: 0.75rem 1rem;
  border: 1px solid #edf0f4;
  border-radius: 8px;
}
::v-deep .stale-select-col {
  width: 48px;
}
</style>
